import type { LocalizedDict } from '../index'

type FormatCopy = {
  dialogTitle: string
  badge: string
  templateLine: string
  templateRow: string
  flowTitle?: string
  flowText?: string
  pageFlow?: string
}

export type DocumentStudioStrings = {
  /** Export type / scope labels (shown with CSS capitalize). */
  exportType: { full: string; category: string; selected_products: string }
  /** Raw run status labels (badge on Catalogs page). */
  runStatus: { queued: string; running: string; completed: string; failed: string }
  priceTier: { internal: string; partner: string; reseller: string; b2b: string }
  createCatalogPdf: string
  noExportsYet: string
  loading: string
  catalogFallback: (id: string) => string
  catalogPreview: string
  wizard: {
    steps: { format: string; type: string; scope: string; content: string; branding: string; review: string }
    formats: { v1: FormatCopy; v2: FormatCopy; v3: FormatCopy }
    chooseFormat: string
    stepAria: (current: number, total: number, label: string) => string
    chooseFormatTitle: string
    chooseFormatHint: string
    currentFormat: string
    professionalV1: string
    professionalV1Desc: string
    editorialBadge: string
    editorialV2: string
    editorialV2Desc: string
    showcaseBadge: string
    showcaseV3: string
    showcaseV3Desc: string
    coverQuestion: string
    typeOptions: {
      full: [string, string]
      category: [string, string]
      selected_products: [string, string]
    }
    fullScopeNote: string
    selectCategories: string
    noCategories: string
    pickProducts: (n: number) => string
    noProducts: string
    showPrices: string
    showPricesHint: string
    priceTierLabel: string
    noCostData: string
    includeComingSoon: string
    includeComingSoonHint: string
    includeIntro: string
    includeIntroHint: string
    template: string
    branding: string
    brandingHint: string
    titlePlaceholder: string
    livePreview: string
    refreshing: string
    previewFailed: (msg: string) => string
    buildingPreview: string
    previewNote: (editorial: boolean) => string
    rows: {
      scope: string
      categories: string
      products: string
      prices: string
      comingSoon: string
      pageFlow: string
      introPages: string
      template: string
    }
    pricesVisible: (tier: string) => string
    hidden: string
    listedAtEnd: string
    omitted: string
    included: string
    cancel: string
    back: string
    next: string
    queuing: string
    generatePdf: string
  }
  catalogs: {
    title: string
    subtitle: string
    exportV2: string
    exportV3: string
    liveDataTitle: string
    liveDataText: string
    formatsTitle: string
    formatsText: string
    asyncTitle: string
    asyncText: string
    recentExports: string
    viewAll: string
    productsCount: (n: number) => string
  }
  brandSettings: {
    saved: string
    loading: string
    title: string
    subtitle: string
    companyName: string
    tagline: string
    brandColor: string
    wordmark: string
    fontFamily: string
    contactEmail: string
    website: string
    channels: string
    saving: string
    save: string
  }
  generatedFiles: {
    statusLabel: { queued: string; running: string; completed: string; failed: string }
    title: string
    subtitle: string
    refresh: string
    queuedNotice: string
    columns: { catalog: string; scope: string; products: string; status: string; created: string; actions: string }
    previewAria: (name: string) => string
    downloadAria: (name: string) => string
  }
  brandPages: {
    title: string
    templateBadge: string
    intro: string
    blocks: [string, string][]
  }
}

export const documentStudioI18n: LocalizedDict<DocumentStudioStrings> = {
  en: {
    exportType: { full: 'full', category: 'category', selected_products: 'selected products' },
    runStatus: { queued: 'queued', running: 'running', completed: 'completed', failed: 'failed' },
    priceTier: { internal: 'internal', partner: 'partner', reseller: 'reseller', b2b: 'b2b' },
    createCatalogPdf: 'Create Catalog PDF',
    noExportsYet: 'No exports yet. Create your first catalog PDF.',
    loading: 'Loading…',
    catalogFallback: (id) => `Catalog ${id}`,
    catalogPreview: 'Catalog preview',
    wizard: {
      steps: {
        format: 'Format',
        type: 'Type',
        scope: 'Scope',
        content: 'Content',
        branding: 'Branding',
        review: 'Review',
      },
      formats: {
        v1: {
          dialogTitle: 'Create Catalog PDF',
          badge: 'Professional · V1',
          templateLine: 'Professional (v1) · landscape, 4 × 2 product grid',
          templateRow: 'Professional (v1)',
        },
        v2: {
          dialogTitle: 'Export Catalog V2',
          badge: 'Editorial Grid · V2',
          templateLine: 'Editorial Grid (v2) · dynamic mosaic cover',
          templateRow: 'Editorial Grid (v2)',
          flowTitle: 'Editorial product flow',
          flowText:
            'Dynamic cover, compact index, then 9 products per page. Products without a usable image are omitted.',
          pageFlow: 'Cover · index · products',
        },
        v3: {
          dialogTitle: 'Export Catalog V3',
          badge: 'Showcase · V3',
          templateLine: 'Showcase (v3) · one product per page, every color on its own photo',
          templateRow: 'Showcase (v3)',
          flowTitle: 'Showcase product flow',
          flowText:
            'Cover, contents and a section opener, then one page per product with each color on its own photo tile. Colors without a photo show a named swatch; products without any image are omitted.',
          pageFlow: 'Cover · contents · openers · products · how to order',
        },
      },
      chooseFormat: 'Choose format',
      stepAria: (current, total, label) => `Step ${current} of ${total}: ${label}`,
      chooseFormatTitle: 'Choose your catalog format',
      chooseFormatHint: 'Your products and filters stay the same. Only the PDF layout changes.',
      currentFormat: 'Current format',
      professionalV1: 'Professional V1',
      professionalV1Desc: 'Landscape pages, 4 × 2 product grid, optional introduction pages.',
      editorialBadge: 'Editorial',
      editorialV2: 'Editorial V2',
      editorialV2Desc: 'Dynamic cover, compact contents, and a 9-product editorial grid.',
      showcaseBadge: 'Showcase',
      showcaseV3: 'Showcase V3',
      showcaseV3Desc:
        'One product per page, every color on its own photo, and a closing how-to-order page.',
      coverQuestion: 'What should this catalog cover?',
      typeOptions: {
        full: ['Full catalog', 'Every published product, grouped by category.'],
        category: ['By category', 'Pick categories / subcategories to include.'],
        selected_products: ['Selected products', 'Hand-pick specific products.'],
      },
      fullScopeNote:
        'The entire published catalog will be included. Continue to content options.',
      selectCategories: 'Select categories to include.',
      noCategories: 'No categories found.',
      pickProducts: (n) => `Pick products (${n} selected).`,
      noProducts: 'No products found.',
      showPrices: 'Show prices',
      showPricesHint: 'Off = names + variation labels only (no price).',
      priceTierLabel: 'Price tier',
      noCostData: 'Cost and supplier data are never included.',
      includeComingSoon: 'Include coming-soon products',
      includeComingSoonHint:
        'Off = only products that can ship today. On = they are listed after every stocked product, in a final “Coming Soon” section.',
      includeIntro: 'Include intro pages',
      includeIntroHint: 'Cover, about, system overview, fulfillment, personalization.',
      template: 'Template',
      branding: 'Branding',
      brandingHint: 'Uses the default brand profile. Edit it under Brand Settings.',
      titlePlaceholder: 'Catalog title (optional)',
      livePreview: 'Live preview',
      refreshing: '· refreshing…',
      previewFailed: (msg) => `Preview failed: ${msg}`,
      buildingPreview: 'Building preview…',
      previewNote: (editorial) =>
        `${editorial ? 'Full-page A4' : 'Layout'} preview (product images appear in the final PDF).`,
      rows: {
        scope: 'Scope',
        categories: 'Categories',
        products: 'Products',
        prices: 'Prices',
        comingSoon: 'Coming soon',
        pageFlow: 'Page flow',
        introPages: 'Intro pages',
        template: 'Template',
      },
      pricesVisible: (tier) => `visible (${tier})`,
      hidden: 'hidden',
      listedAtEnd: 'listed at the end',
      omitted: 'omitted',
      included: 'included',
      cancel: 'Cancel',
      back: 'Back',
      next: 'Next',
      queuing: 'Queuing…',
      generatePdf: 'Generate PDF',
    },
    catalogs: {
      title: 'Catalogs',
      subtitle: 'Generate print-quality catalog PDFs from your live catalog.',
      exportV2: 'Export V2',
      exportV3: 'Export V3',
      liveDataTitle: 'Live catalog data',
      liveDataText:
        'Product pages are built from your published catalog — cost and supplier data are never included.',
      formatsTitle: 'Three catalog formats',
      formatsText:
        'Professional V1 stays available. Export V2 is the mosaic cover and 3 × 3 grid; Export V3 gives every product its own page with a photo per color.',
      asyncTitle: 'Async generation',
      asyncText: 'PDFs render in the background. Track progress and download under Generated Files.',
      recentExports: 'Recent exports',
      viewAll: 'View all →',
      productsCount: (n) => `${n} products`,
    },
    brandSettings: {
      saved: 'Brand profile saved',
      loading: 'Loading brand profile…',
      title: 'Brand Settings',
      subtitle: 'These tokens are used by the catalog PDF renderer.',
      companyName: 'Company name',
      tagline: 'Tagline',
      brandColor: 'Brand color',
      wordmark: 'Wordmark',
      fontFamily: 'Font family',
      contactEmail: 'Contact email',
      website: 'Website',
      channels: 'Channels (comma-separated)',
      saving: 'Saving…',
      save: 'Save brand',
    },
    generatedFiles: {
      statusLabel: {
        queued: 'Waiting to start',
        running: 'Generating PDF',
        completed: 'Ready to download',
        failed: 'Generation failed',
      },
      title: 'Generated Files',
      subtitle: 'Catalog PDF exports and their status.',
      refresh: 'Refresh',
      queuedNotice:
        'A queued export usually starts within 1 minute. This page refreshes automatically.',
      columns: {
        catalog: 'Catalog',
        scope: 'Scope',
        products: 'Products',
        status: 'Status',
        created: 'Created',
        actions: 'Actions',
      },
      previewAria: (name) => `Preview ${name}`,
      downloadAria: (name) => `Download ${name}`,
    },
    brandPages: {
      title: 'Brand Pages',
      templateBadge: 'template v1',
      intro:
        'These versioned intro blocks open every catalog PDF. Product and category pages are generated from live catalog data after them. Editing intro copy is coming in a later phase.',
      blocks: [
        ['Cover', 'Wordmark, tagline, key stats, and channels.'],
        ['About', 'Who Pexify is and what the platform does.'],
        ['System overview', 'One operations hub — an inline infographic.'],
        ['Fulfillment model', 'In-stock vs made-to-order — no stock risk.'],
        ['Personalization', 'Four in-house techniques with best-for guidance.'],
        ['Contents', 'Category list with product counts (from live data).'],
        ['Final CTA', 'How to apply / start selling.'],
      ],
    },
  },
  tr: {
    exportType: { full: 'tam katalog', category: 'kategori', selected_products: 'seçili ürünler' },
    runStatus: { queued: 'sırada', running: 'hazırlanıyor', completed: 'tamamlandı', failed: 'başarısız' },
    priceTier: { internal: 'iç', partner: 'partner', reseller: 'bayi', b2b: 'b2b' },
    createCatalogPdf: 'Katalog PDF oluştur',
    noExportsYet: 'Henüz dışa aktarım yok. İlk katalog PDF’inizi oluşturun.',
    loading: 'Yükleniyor…',
    catalogFallback: (id) => `Katalog ${id}`,
    catalogPreview: 'Katalog önizlemesi',
    wizard: {
      steps: {
        format: 'Biçim',
        type: 'Tür',
        scope: 'Kapsam',
        content: 'İçerik',
        branding: 'Marka',
        review: 'Kontrol',
      },
      formats: {
        v1: {
          dialogTitle: 'Katalog PDF oluştur',
          badge: 'Profesyonel · V1',
          templateLine: 'Profesyonel (v1) · yatay, 4 × 2 ürün ızgarası',
          templateRow: 'Profesyonel (v1)',
        },
        v2: {
          dialogTitle: 'Katalog V2 dışa aktar',
          badge: 'Editoryal Izgara · V2',
          templateLine: 'Editoryal Izgara (v2) · dinamik mozaik kapak',
          templateRow: 'Editoryal Izgara (v2)',
          flowTitle: 'Editoryal ürün akışı',
          flowText:
            'Dinamik kapak, kısa dizin, ardından sayfa başına 9 ürün. Kullanılabilir görseli olmayan ürünler eklenmez.',
          pageFlow: 'Kapak · dizin · ürünler',
        },
        v3: {
          dialogTitle: 'Katalog V3 dışa aktar',
          badge: 'Vitrin · V3',
          templateLine: 'Vitrin (v3) · sayfa başına bir ürün, her renk kendi fotoğrafında',
          templateRow: 'Vitrin (v3)',
          flowTitle: 'Vitrin ürün akışı',
          flowText:
            'Kapak, içindekiler ve bölüm açılışı, ardından her ürün için bir sayfa; her renk kendi fotoğraf kutusunda. Fotoğrafı olmayan renkler adıyla renk örneği olarak görünür; hiç görseli olmayan ürünler eklenmez.',
          pageFlow: 'Kapak · içindekiler · açılışlar · ürünler · nasıl sipariş verilir',
        },
      },
      chooseFormat: 'Biçim seçin',
      stepAria: (current, total, label) => `Adım ${current} / ${total}: ${label}`,
      chooseFormatTitle: 'Katalog biçiminizi seçin',
      chooseFormatHint: 'Ürünleriniz ve filtreleriniz aynı kalır. Yalnızca PDF düzeni değişir.',
      currentFormat: 'Mevcut biçim',
      professionalV1: 'Profesyonel V1',
      professionalV1Desc: 'Yatay sayfalar, 4 × 2 ürün ızgarası, isteğe bağlı tanıtım sayfaları.',
      editorialBadge: 'Editoryal',
      editorialV2: 'Editoryal V2',
      editorialV2Desc: 'Dinamik kapak, kısa içindekiler ve 9 ürünlük editoryal ızgara.',
      showcaseBadge: 'Vitrin',
      showcaseV3: 'Vitrin V3',
      showcaseV3Desc:
        'Sayfa başına bir ürün, her renk kendi fotoğrafında ve sonda nasıl sipariş verilir sayfası.',
      coverQuestion: 'Bu katalog neleri kapsasın?',
      typeOptions: {
        full: ['Tüm katalog', 'Yayındaki tüm ürünler, kategoriye göre gruplu.'],
        category: ['Kategoriye göre', 'Eklenecek kategori / alt kategorileri seçin.'],
        selected_products: ['Seçili ürünler', 'Belirli ürünleri tek tek seçin.'],
      },
      fullScopeNote: 'Yayındaki tüm katalog eklenecek. İçerik seçeneklerine geçin.',
      selectCategories: 'Eklenecek kategorileri seçin.',
      noCategories: 'Kategori bulunamadı.',
      pickProducts: (n) => `Ürün seçin (${n} seçili).`,
      noProducts: 'Ürün bulunamadı.',
      showPrices: 'Fiyatları göster',
      showPricesHint: 'Kapalı = yalnızca adlar + varyasyon etiketleri (fiyat yok).',
      priceTierLabel: 'Fiyat kademesi',
      noCostData: 'Maliyet ve tedarikçi bilgisi asla eklenmez.',
      includeComingSoon: 'Yakında gelecek ürünleri ekle',
      includeComingSoonHint:
        'Kapalı = yalnızca bugün gönderilebilen ürünler. Açık = stoktaki tüm ürünlerden sonra, en sondaki “Yakında” bölümünde listelenir.',
      includeIntro: 'Tanıtım sayfalarını ekle',
      includeIntroHint: 'Kapak, hakkımızda, sistem özeti, gönderim, kişiselleştirme.',
      template: 'Şablon',
      branding: 'Marka',
      brandingHint: 'Varsayılan marka profilini kullanır. Marka Ayarları’ndan düzenleyin.',
      titlePlaceholder: 'Katalog başlığı (isteğe bağlı)',
      livePreview: 'Canlı önizleme',
      refreshing: '· yenileniyor…',
      previewFailed: (msg) => `Önizleme başarısız: ${msg}`,
      buildingPreview: 'Önizleme hazırlanıyor…',
      previewNote: (editorial) =>
        `${editorial ? 'Tam sayfa A4' : 'Düzen'} önizlemesi (ürün görselleri son PDF’te görünür).`,
      rows: {
        scope: 'Kapsam',
        categories: 'Kategoriler',
        products: 'Ürünler',
        prices: 'Fiyatlar',
        comingSoon: 'Yakında',
        pageFlow: 'Sayfa akışı',
        introPages: 'Tanıtım sayfaları',
        template: 'Şablon',
      },
      pricesVisible: (tier) => `görünür (${tier})`,
      hidden: 'gizli',
      listedAtEnd: 'sonda listelenir',
      omitted: 'eklenmez',
      included: 'eklenir',
      cancel: 'Vazgeç',
      back: 'Geri',
      next: 'İleri',
      queuing: 'Sıraya alınıyor…',
      generatePdf: 'PDF oluştur',
    },
    catalogs: {
      title: 'Kataloglar',
      subtitle: 'Canlı kataloğunuzdan baskı kalitesinde katalog PDF’leri oluşturun.',
      exportV2: 'V2 dışa aktar',
      exportV3: 'V3 dışa aktar',
      liveDataTitle: 'Canlı katalog verisi',
      liveDataText:
        'Ürün sayfaları yayındaki kataloğunuzdan hazırlanır — maliyet ve tedarikçi bilgisi asla eklenmez.',
      formatsTitle: 'Üç katalog biçimi',
      formatsText:
        'Profesyonel V1 kullanılmaya devam eder. V2 mozaik kapak ve 3 × 3 ızgaradır; V3 her ürüne, her renk için bir fotoğrafla kendi sayfasını verir.',
      asyncTitle: 'Arka planda hazırlama',
      asyncText: 'PDF’ler arka planda hazırlanır. İlerlemeyi Oluşturulan Dosyalar’da izleyip indirin.',
      recentExports: 'Son dışa aktarımlar',
      viewAll: 'Tümünü gör →',
      productsCount: (n) => `${n} ürün`,
    },
    brandSettings: {
      saved: 'Marka profili kaydedildi',
      loading: 'Marka profili yükleniyor…',
      title: 'Marka Ayarları',
      subtitle: 'Bu değerler katalog PDF’i hazırlanırken kullanılır.',
      companyName: 'Şirket adı',
      tagline: 'Slogan',
      brandColor: 'Marka rengi',
      wordmark: 'Yazı logo',
      fontFamily: 'Yazı tipi',
      contactEmail: 'İletişim e-postası',
      website: 'Web sitesi',
      channels: 'Kanallar (virgülle ayırın)',
      saving: 'Kaydediliyor…',
      save: 'Markayı kaydet',
    },
    generatedFiles: {
      statusLabel: {
        queued: 'Başlaması bekleniyor',
        running: 'PDF hazırlanıyor',
        completed: 'İndirmeye hazır',
        failed: 'Hazırlanamadı',
      },
      title: 'Oluşturulan Dosyalar',
      subtitle: 'Katalog PDF dışa aktarımları ve durumları.',
      refresh: 'Yenile',
      queuedNotice:
        'Sıradaki bir dışa aktarım genelde 1 dakika içinde başlar. Bu sayfa kendiliğinden yenilenir.',
      columns: {
        catalog: 'Katalog',
        scope: 'Kapsam',
        products: 'Ürünler',
        status: 'Durum',
        created: 'Oluşturulma',
        actions: 'İşlemler',
      },
      previewAria: (name) => `${name} önizle`,
      downloadAria: (name) => `${name} indir`,
    },
    brandPages: {
      title: 'Marka Sayfaları',
      templateBadge: 'şablon v1',
      intro:
        'Bu sürümlü tanıtım blokları her katalog PDF’inin başında yer alır. Ürün ve kategori sayfaları bunlardan sonra canlı katalog verisinden hazırlanır. Tanıtım metnini düzenleme sonraki bir aşamada gelecek.',
      blocks: [
        ['Kapak', 'Yazı logo, slogan, önemli rakamlar ve kanallar.'],
        ['Hakkımızda', 'Pexify kimdir ve platform ne yapar.'],
        ['Sistem özeti', 'Tek operasyon merkezi — sayfa içi bilgi grafiği.'],
        ['Gönderim modeli', 'Stoktan ve siparişe üretim — stok riski yok.'],
        ['Kişiselleştirme', 'Kendi atölyemizdeki dört teknik ve hangisi neye uygun.'],
        ['İçindekiler', 'Ürün sayılarıyla kategori listesi (canlı veriden).'],
        ['Son çağrı', 'Nasıl başvurulur / satışa nasıl başlanır.'],
      ],
    },
  },
}
